<template lang="pug">
  div(v-if="isHouse(getDeliveryPropertyType)" class="mt-8 lg:mt-16")
    VeeField(
      name="deliveryPropertyType"
      v-slot="{ field }"
    )
      SelRefined(
        v-bind="field"
        :value="getDeliveryPropertyType"
        @input="handlePropertyType"
        :options="getPropertyTypesHouse"
        option-text="name"
        option-value="value"
        :placeholder="$t('location_form.select_property_type')"
        :initial-open="true"
        :sub-menu="true"
        indicator
        qa-tag="sel_delivery_prop_type_house"
      )
      VeeErrorMessage(name="deliveryPropertyType")
        TextInputError(class="my-8")
          | {{ $t('location_form.error.property_type_to') }}
  div(v-else-if="isFlat(getDeliveryPropertyType)" class="mt-8 lg:mt-16")
    VeeField(
      name="deliveryPropertyType"
      v-slot="{ field }"
    )
      SelRefined(
        v-bind="field"
        :value="getDeliveryPropertyType"
        @input="handlePropertyType"
        :options="getPropertyTypesFlat"
        option-text="name"
        option-value="value"
        :placeholder="$t('location_form.select_property_type')"
        :initial-open="true"
        :sub-menu="true"
        indicator
        qa-tag="sel_delivery_prop_type_flat"
      )
      VeeErrorMessage(name="deliveryPropertyType")
        TextInputError(class="my-8")
          | {{ $t('location_form.error.property_type_to') }}

    SelRefined(
      v-if="!propertyFloorHidden && requiresFloor(getDeliveryPropertyType)"
      :value="getDeliveryFloor"
      @input="handleDeliveryFloor"
      class="mt-8 lg:mt-16"
      :options="getFloors"
      option-text="name"
      option-value="value"
      indicator
      qa-tag="sel_delivery_floor")

    SelRefined(
      v-if="requiresFloor(getDeliveryPropertyType) && getDeliveryFloor !== 'Ground floor'"
      :value="liftAvailable"
      @input="handleLiftAvailable"
      class="mt-8 lg:mt-16"
      :options="getLiftAvailableOptions"
      option-text="name"
      option-value="value"
      indicator
      qa-tag="sel_delivery_lift")

  div(v-else-if="!propertyTypeHidden" class="mt-8 lg:mt-16")
    VeeField(
      name="deliveryPropertyType"
      v-slot="{ field }"
    )
      SelRefined(
        v-bind="field"
        :value="getDeliveryPropertyType"
        @input="handlePropertyType"
        :options="getPropertyTypesMain"
        option-text="name"
        option-value="value"
        :placeholder="$t('location_form.select_property_type')"
        :initial-open="getDeliveryPropertyType === ''"
        indicator
        qa-tag="sel_delivery_prop_type")
      VeeErrorMessage(name="deliveryPropertyType")
        TextInputError(class="my-8")
          | {{ $t('location_form.error.property_type_to') }}

    SelRefined(
      v-if="!propertyFloorHidden && requiresFloor(getDeliveryPropertyType)"
      :value="getDeliveryFloor"
      @input="handleDeliveryFloor"
      class="mt-8 lg:mt-16"
      :options="getFloors"
      option-text="name"
      option-value="value"
      indicator
      qa-tag="sel_delivery_floor")

    SelRefined(
      v-if="requiresFloor(getDeliveryPropertyType) && getDeliveryFloor !== 'Ground floor'"
      :value="liftAvailable"
      @input="handleLiftAvailable"
      class="mt-8 lg:mt-16"
      :options="getLiftAvailableOptions"
      option-text="name"
      option-value="value"
      indicator
      qa-tag="sel_delivery_lift")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { mapGetters, mapActions } from "vuex"
import { getAttributes } from "~/utils/block"
import {
  SET_DELIVERY_FLOOR,
  SET_DELIVERY_PROPERTY_TYPE,
  SET_LISTING,
  SET_DELIVERY_LIFT_AVAILABLE,
  GET_DELIVERY_FLOOR,
  GET_DELIVERY_LIFT_AVAILABLE,
  GET_DELIVERY_PROPERTY_TYPE,
  state as InitialListing
} from "~/store/listing"
import { isFlat, isHouse } from "~/services/public/Listing"
import {
  FLOOR_GROUND,
  FLOORS,
  LIFT_AVAILABLE,
  LIFT_UNAVAILABLE,
  PROPERTY_TYPE_LIST_FLAT,
  PROPERTY_TYPE_LIST_HOUSE,
  PROPERTY_TYPE_LIST_MAIN
} from "~/constants/listing"
import {
  GET_FLOORS,
  GET_PROPERTY_TYPES,
  GET_PROPERTY_TYPES_MAIN,
  GET_PROPERTY_TYPES_FLAT,
  GET_PROPERTY_TYPES_HOUSE
} from "~/store/property"
import { swapFirstTwoElements } from "~/scripts/useful-functions"
import { LOCALE } from "~/constants/locale"

interface ComponentData {
  propertyTypesMain: any[]
  propertyTypesHouse: any[]
  propertyTypesFlat: any[]
  floors: any[]
}

interface LiftOption {
  name: string
  value: number
}

export default defineComponent({
  name: "PropertyDeliveryInput",

  props: {
    propertyTypeHidden: {
      type: Boolean
    },
    propertyFloorHidden: {
      type: Boolean
    },
    block: {
      type: Object,
      required: true
    },
    virgin: {
      type: Boolean
    },
    lazy: {
      type: Boolean,
      default: true
    }
  },

  data(): ComponentData {
    return {
      propertyTypesMain: PROPERTY_TYPE_LIST_MAIN,
      propertyTypesHouse: PROPERTY_TYPE_LIST_HOUSE,
      propertyTypesFlat: PROPERTY_TYPE_LIST_FLAT,
      floors: FLOORS
    }
  },

  computed: {
    ...mapGetters("listing", {
      getDeliveryFloor: GET_DELIVERY_FLOOR,
      getDeliveryLiftAvailable: GET_DELIVERY_LIFT_AVAILABLE,
      getDeliveryPropertyType: GET_DELIVERY_PROPERTY_TYPE
    }),
    ...mapGetters("property", {
      getStorePropertyTypes: GET_PROPERTY_TYPES,
      getStorePropertyTypesMain: GET_PROPERTY_TYPES_MAIN,
      getStorePropertyTypesHouse: GET_PROPERTY_TYPES_HOUSE,
      getStorePropertyTypesFlat: GET_PROPERTY_TYPES_FLAT,
      getStoreFloors: GET_FLOORS
    }),

    getPropertyTypesMain(): any[] {
      if (!this.getStorePropertyTypesMain) return this.propertyTypesMain

      const propertyTypesToShow = this.getStorePropertyTypesMain.filter(type => !type.hidden)

      if (this.$i18n.locale === LOCALE.UK) {
        return propertyTypesToShow
      }
      if (this.$i18n.locale === LOCALE.SPAIN) {
        return swapFirstTwoElements(propertyTypesToShow)
      }
      return propertyTypesToShow
    },

    getPropertyTypesHouse(): any[] {
      return this.getStorePropertyTypesHouse || this.propertyTypesHouse
    },

    getPropertyTypesFlat(): any[] {
      return this.getStorePropertyTypesFlat || this.propertyTypesFlat
    },

    getFloors(): any[] {
      return this.getStoreFloors || this.floors
    },

    attributes(): Record<string, any> | null {
      return this.virgin ? null : this.getAttributes(this.block)
    },

    liftAvailable(): number {
      if (this.getDeliveryLiftAvailable === null) {
        this.setLiftAvailable(LIFT_AVAILABLE)
        return LIFT_AVAILABLE
      }
      return this.getDeliveryLiftAvailable
    },

    getLiftAvailableOptions(): LiftOption[] {
      return [
        {
          name: this.$t("location_form.lift_available") as string,
          value: LIFT_AVAILABLE
        },
        {
          name: this.$t("location_form.lift_not_available") as string,
          value: LIFT_UNAVAILABLE
        }
      ]
    }
  },

  created() {
    this.setListing(InitialListing())
  },

  methods: {
    ...mapActions("listing", {
      setDeliveryPropertyType: SET_DELIVERY_PROPERTY_TYPE,
      setDeliveryFloor: SET_DELIVERY_FLOOR,
      setLiftAvailable: SET_DELIVERY_LIFT_AVAILABLE,
      setListing: SET_LISTING
    }),

    handlePropertyType(value: string) {
      if (!this.isFlat(value) || this.getDeliveryFloor === FLOOR_GROUND) {
        this.setDeliveryFloor(FLOOR_GROUND)
        this.setLiftAvailable(null)
      }
      this.setDeliveryPropertyType(value)
    },

    handleDeliveryFloor(value: string | null): void {
      if (value === FLOOR_GROUND) {
        this.setLiftAvailable(null)
      }
      this.setDeliveryFloor(value)
    },

    handleLiftAvailable(value: string | null): void {
      this.setLiftAvailable(value)
    },

    getAttributes,

    isHouse(propertyType: string): boolean {
      return isHouse(propertyType) as boolean
    },

    isFlat(propertyType: string): boolean {
      return isFlat(propertyType) as boolean
    },

    requiresFloor(propertyType: string): boolean {
      if (!this.getStorePropertyTypes) {
        return this.propertyTypeHidden && !this.propertyFloorHidden
      }
      const propertyTypeObject = this.getStorePropertyTypes.find(
        storePropertyType => storePropertyType.value === propertyType
      )
      return !!propertyTypeObject?.propertyWithLevels
    }
  }
})
</script>

<style lang="css" module></style>
